import React, { useEffect, useState } from 'react';
import { Spinner, Container, Row, Col } from 'reactstrap';
import { useTranslator, useToast } from '@front-activerse/common-components';
import {
  WearableConnectionsConnectorApollo,
  WearableProvidersConnectorApollo,
  InitiateWearableConnectionConnectorApollo,
  DisconnectWearableConnectorApollo,
  ConfirmConnectionConnectorApollo,
  TriggerSyncConnectorApollo,
} from '@front-activerse/common-components';
import type { WearableMutationResult } from '@front-activerse/common-components';
import Layout from '../Layout/Layout';
import TitleBar from '../Layout/TitleBar';
import type { WearableConnection, WearableProvider } from './types';
import ProviderCard from './ProviderCard';

interface Props {
  redirectUri: string;
}

interface InnerProps {
  providers: WearableProvider[];
  providersLoading: boolean;
  providersError: boolean;
  connections: WearableConnection[];
  loading: boolean;
  initiateConnection: (
    providerKey: string,
    redirectUri: string
  ) => Promise<{ code: string; authorizationUrl?: string } | null>;
  connecting: boolean;
  disconnectProvider: (providerKey: string) => Promise<WearableMutationResult>;
  disconnecting: boolean;
  confirmConnection: (providerKey: string) => Promise<WearableMutationResult>;
  triggerSync: (providerKey: string) => Promise<WearableMutationResult>;
  syncing: boolean;
  redirectUri: string;
}

const WearablesInner: React.FC<InnerProps> = ({
  providers,
  providersLoading,
  providersError,
  connections,
  loading,
  initiateConnection,
  connecting,
  disconnectProvider,
  disconnecting,
  confirmConnection,
  triggerSync,
  syncing,
  redirectUri,
}) => {
  const { t } = useTranslator();
  const { toastError } = useToast();
  const [confirmingProviders, setConfirmingProviders] = useState<Set<string>>(new Set());

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const providerToConfirm = params.get('confirm');

    if (!providerToConfirm) return;

    window.history.replaceState(null, '', window.location.pathname);

    setConfirmingProviders((prev) => new Set([...prev, providerToConfirm]));
    confirmConnection(providerToConfirm)
      .then((result) => {
        if (!result.ok) toastError();
      })
      .finally(() => {
        setConfirmingProviders((prev) => {
          const next = new Set(prev);
          next.delete(providerToConfirm);
          return next;
        });
      });
    // Run once on mount — confirmConnection is a fresh ref each render from the
    // Apollo render-prop connector, which would retrigger the OAuth callback.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connectionMap = connections.reduce<Record<string, WearableConnection>>((acc, conn) => {
    acc[conn.provider.openWearablesProvider] = conn;
    return acc;
  }, {});

  const handleConnect = async (providerKey: string) => {
    const callbackUri = `${redirectUri}?confirm=${providerKey}`;
    const result = await initiateConnection(providerKey, callbackUri);
    if (result?.authorizationUrl) {
      window.location.href = result.authorizationUrl;
    }
  };

  const handleDisconnect = async (providerKey: string) => {
    const result = await disconnectProvider(providerKey);
    if (!result.ok) toastError();
  };

  const handleSync = async (providerKey: string) => {
    const result = await triggerSync(providerKey);
    if (!result.ok) toastError();
  };

  return (
    <Layout>
      <TitleBar showBackBtn={false}>{t('menu.wearables')}</TitleBar>
      <section className="page-template-wearable-integrations full-height3a">
        <Container className="py-xl">
          <Row>
            <Col md={{ size: 8, offset: 2 }}>
              {loading || providersLoading ? (
                <div className="d-flex justify-content-center py-xxl">
                  <Spinner />
                </div>
              ) : providersError ? (
                <div
                  className="text-center py-xxl text-text-variant-color"
                  data-testid="wearables-catalog-error"
                >
                  {t('wearables.catalog.error')}
                </div>
              ) : providers.length === 0 ? (
                <div
                  className="text-center py-xxl text-text-variant-color"
                  data-testid="wearables-catalog-empty"
                >
                  {t('wearables.catalog.empty')}
                </div>
              ) : (
                providers.map((provider) => (
                  <ProviderCard
                    key={provider.openWearablesProvider}
                    provider={provider}
                    connection={connectionMap[provider.openWearablesProvider]}
                    onConnect={handleConnect}
                    onDisconnect={handleDisconnect}
                    onSync={handleSync}
                    connecting={connecting}
                    disconnecting={disconnecting}
                    syncing={syncing}
                    isConfirming={confirmingProviders.has(provider.openWearablesProvider)}
                  />
                ))
              )}
            </Col>
          </Row>
        </Container>
      </section>
    </Layout>
  );
};

const WearablesContainer: React.FC<Props> = ({ redirectUri }) => {
  return (
    <WearableProvidersConnectorApollo>
      {({ providers, loading: providersLoading, error: providersError }) => (
        <WearableConnectionsConnectorApollo>
          {({ loading, connections }) => (
            <InitiateWearableConnectionConnectorApollo>
              {({ initiateConnection, loading: connecting }) => (
                <DisconnectWearableConnectorApollo>
                  {({ disconnectProvider, loading: disconnecting }) => (
                    <ConfirmConnectionConnectorApollo>
                      {({ confirmConnection }) => (
                        <TriggerSyncConnectorApollo>
                          {({ triggerSync, loading: syncing }) => (
                            <WearablesInner
                              providers={providers}
                              providersLoading={providersLoading}
                              providersError={providersError}
                              connections={connections}
                              loading={loading}
                              initiateConnection={initiateConnection}
                              connecting={connecting}
                              disconnectProvider={disconnectProvider}
                              disconnecting={disconnecting}
                              confirmConnection={confirmConnection}
                              triggerSync={triggerSync}
                              syncing={syncing}
                              redirectUri={redirectUri}
                            />
                          )}
                        </TriggerSyncConnectorApollo>
                      )}
                    </ConfirmConnectionConnectorApollo>
                  )}
                </DisconnectWearableConnectorApollo>
              )}
            </InitiateWearableConnectionConnectorApollo>
          )}
        </WearableConnectionsConnectorApollo>
      )}
    </WearableProvidersConnectorApollo>
  );
};

export default WearablesContainer;
